import { Card, Text } from "@overmap-ai/blocks"
import { type IssueAssociation, type Stored, UNTITLED_ISSUE_TITLE } from "@overmap-ai/common"
import { selectIssueById, selectIssueTypeStatusById, selectUserById } from "@overmap-ai/core"
import { Link } from "@tanstack/react-router"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { AssetTimelineItemType } from "../../enums/assets"
import { assetTimelineItemTypeData } from "../../mappings/assets"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import { IssueTypeStatusIcon } from "../Icons/IssueTypeStatusIcon"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import { TimelineIconRail } from "../TimelineIconRail/TimelineIconRail"
import type { AssetTimelineSize } from "./typings"

interface AssetIssueAssociationTimelineItemProps {
	issueAssociation: Stored<IssueAssociation>
	size: AssetTimelineSize
}

export const AssetIssueAssociationTimelineItem = memo<AssetIssueAssociationTimelineItemProps>((props) => {
	const { issueAssociation, size } = props

	const issue = useAppSelector(selectIssueById(issueAssociation.issue))!
	const createdBy = useAppSelector(selectUserById(issueAssociation.created_by ?? -1))
	const issueTypeStatus = useAppSelector(selectIssueTypeStatusById(issue.status))!

	const { icon } = assetTimelineItemTypeData[AssetTimelineItemType.ISSUE_ASSOCIATION]

	return (
		<div className="flex h-max w-full gap-4">
			<TimelineIconRail icon={icon} size={size} />
			<Card className="flex min-w-0 grow flex-col gap-1" size={size}>
				<Text size={size} accentColor="base">
					<Text highContrast>{getUserDisplayLabel(createdBy)}</Text>
					&nbsp;linked issue
					<Text accentColor="base">
						&nbsp;&#8226;&nbsp;{formatCompactDate(issueAssociation.submitted_at)}
					</Text>
				</Text>

				<Text accentColor="base" className="min-w-0 truncate" size={size}>
					<IssueTypeIcon issueTypeId={issue.issue_type} className="align-sub" />
					&nbsp;
					<Link
						className={interactiveTextCva()}
						from="/$organizationId/$projectId"
						to="/$organizationId/$projectId/issues/$issueId"
						params={{
							issueId: issue.uuid,
						}}
					>
						<IssueIndexLabel issueId={issue.uuid} highContrast />
						&nbsp;
						<Text accentColor="base">{issue.title || UNTITLED_ISSUE_TITLE}</Text>
					</Link>
					&nbsp;&nbsp;
					<IssueTypeStatusIcon issueTypeStatusId={issue.status} className="align-sub" />
					&nbsp;
					<Text accentColor="base">{issueTypeStatus.name}</Text>
				</Text>
			</Card>
		</div>
	)
})
AssetIssueAssociationTimelineItem.displayName = "AssetIssueAssociationTimelineItem"
